import { useAppDispatch } from 'redux/createStore'
import { useCRUDUserPopupProviderContext } from '../Provider/CRUDUserPopupProvider'
import { useCallback, useMemo } from 'react'
import { ROLES } from 'features/Permission'
import { TUserForm } from 'components/Forms/forms.user.d'
import { USER_STATUSES } from 'constants/common'
import { getUser } from 'redux/store/user/getters'
import { useSelector } from 'react-redux'
import { VALIDATION_MESSAGES } from 'constants/txt'
import { setLoading } from 'redux/store/common/slice'
import { approveContractCategories } from 'features/ContractCategories/api'
import { notification } from 'components/Notification'

export const useApproveContractCategories = ({
  userData,
  setIsArchiveOpen,
  refreshUserData
}: {
  userData: Partial<TUserForm>
  setIsArchiveOpen: (isOpen: boolean) => void
  refreshUserData: () => void
}) => {
  const { reviewAndApproveCategoriesPopup, activateWithoutCategoriesPopup } =
    useCRUDUserPopupProviderContext()

  const dispatch = useAppDispatch()
  const user = useSelector(getUser)

  const allPendingCategories = useMemo(() => {
    if (userData.role === ROLES.HOSPITAL_USER) {
      return (
        userData.hospitals?.flatMap(
          (hospital) => hospital.contract_categories_waiting_for_approval ?? []
        ) ?? []
      )
    }
    return []
  }, [userData.hospitals, userData.role])

  const pendingCategories = useMemo(() => {
    return allPendingCategories.filter((category) => category.can_be_approved)
  }, [allPendingCategories])

  const isPendingUserNeedsToReviewCategories = useMemo(() => {
    return (
      userData.status === USER_STATUSES.PENDING &&
      allPendingCategories.length > 0
    )
  }, [userData.status, allPendingCategories.length])

  const isCurrentUserCanReviewCategories = useMemo(
    () =>
      user.role === ROLES.CONTRACT_STEWARD &&
      isPendingUserNeedsToReviewCategories &&
      pendingCategories.length > 0,
    [user.role, isPendingUserNeedsToReviewCategories, pendingCategories?.length]
  )

  const onArchiveUserFromCategories = () => {
    reviewAndApproveCategoriesPopup.actions.close()
    setIsArchiveOpen(true)
  }

  const onReviewCategories = () => {
    reviewAndApproveCategoriesPopup.actions.open()
  }

  const onApproveCategories = useCallback(
    async (categories: string[]) => {
      try {
        dispatch(setLoading(true))
        await approveContractCategories(userData.uuid, categories)
        await refreshUserData()
      } catch (err) {
        notification.error({ message: VALIDATION_MESSAGES.SM0141 })
      } finally {
        dispatch(setLoading(false))
        reviewAndApproveCategoriesPopup.actions.close()
        activateWithoutCategoriesPopup.actions.close()
      }
    },
    [
      activateWithoutCategoriesPopup.actions,
      dispatch,
      refreshUserData,
      reviewAndApproveCategoriesPopup.actions,
      userData.uuid
    ]
  )

  return {
    onArchiveUserFromCategories,
    onReviewCategories,
    onApproveCategories,
    isPendingUserNeedsToReviewCategories,
    isCurrentUserCanReviewCategories,
    pendingCategories
  }
}
